import { getHexToRgba } from "@nykaa/payment-ui/src/common/helpers/getHexToRgba";
import { Icon } from "@nykaa/payment-ui/src/Icon";
import { SubTitle } from "@nykaa/payment-ui/src/SubTitle";
import { Title } from "@nykaa/payment-ui/src/Title";
import { getUpdatedNumber } from "helpers/utils/getUpdatedNumber";
import { logger } from "logging";
import React, { useContext } from "react";
import { IFailureDetails } from "../@types";
import { BottomSheetContentLayout, CustomIconLayout, CustomTextLayout } from "../assets/style";
import { get } from "logics/src/utils/helpers";
import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { ICONS_NAMES, getIcons } from "helpers/utils/getIcons";
import WalletFailureMethodComponent from "./WalletFailureMethodComponent";
import { getStringFromId } from "helpers/vernacular";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { MASTER_PAYMENT_STATUS } from "constants/paymentStatus";
import { formatNumber } from "helpers/utils/numberFormatter";

/**
 * component to render the title , subtitle and nudge details on the bottomsheet
 */
export const FailureDetails = (props: IFailureDetails) => {
  const {
    theme,
    totalSaved,
    fallbackMessages: { title, subTitle, imageUrl, nudge },
    isWalletChecked = false,
    handleWalletToggle,
  } = props;
  let updatedNudge = nudge,
    updatedSubTitle = subTitle;
  const paymentContext = useContext(PaymentContext);
  const walletConfig = get(paymentContext, "walletConfig");
  const walletBalance = get(walletConfig, "walletBalance");
  const paymentResponseV2Data = get(paymentContext, "paymentResponseV2Data");
  const walletAmountUsed = get(paymentResponseV2Data, "walletAmountUsed");
  const flowConfig = get(paymentContext, "flowConfig");
  const walletTitle = getStringFromId(
    VERNACULAR_STRING_IDS.USE_WALLET_TITLE,
    VERNACULAR_DEFAULT_MESSAGES.USE_WALLET_TITLE
  );
  const masterPaymentStatus = get(paymentResponseV2Data, "masterPaymentStatus");

  try {
    if (nudge?.includes("<value>") || subTitle?.includes("<value>")) {
      /**
       * if <value> is present in nudge message, replace it totalSaved
       * if totalSaved is not present then remove message
       */
      let updatedTotalSaved = getUpdatedNumber(totalSaved || 0);
      if (nudge?.includes("<value>")) {
        updatedNudge = updatedTotalSaved
          ? nudge.replace("<value>", `<span style='color: ${theme?.colors?.positive}; font-weight: 400'>₹${String(formatNumber(
            updatedTotalSaved
          ))}</span>`)
          : "";
      }
      if (subTitle?.includes("<value>")) {
        if (masterPaymentStatus === MASTER_PAYMENT_STATUS.USER_DROPPED) {
          updatedSubTitle = updatedTotalSaved
            ? subTitle.replace(
                "<value>",
                `<span style='color: ${theme?.colors?.positive}; font-weight: 600'>₹${String(
                  formatNumber(updatedTotalSaved)
                )}</span>`
              )
            : "";
        } else {
          updatedSubTitle = updatedTotalSaved
            ? subTitle.replace("<value>", `₹${String(formatNumber(updatedTotalSaved))}`)
            : "";
        }
      }
    }
  } catch (error) {
    logger.error(error, "Error in FailureDetails");
  }

  return (
    <>
      <CustomIconLayout>
        <Icon icon={imageUrl} onClick={() => {}} theme={theme} />
      </CustomIconLayout>
      <CustomTextLayout>
        <Title
          text={title}
          font={theme?.typography?.titleMedium}
          color={(theme?.colors as any)?.titleColor}
          theme={theme}
          dataTestId="title_bottomsheet"
        />
      </CustomTextLayout>
      <BottomSheetContentLayout>
        {updatedSubTitle && (
          <SubTitle
            htmlText={updatedSubTitle}
            font={theme?.typography?.bodyLarge}
            color={getHexToRgba(theme?.colors?.chipColor, 0.64)}
            theme={theme}
            dataTestId="subTitle_bottomsheet"
          />
        )}

        {/* This condition checks below listed scenarios:
         1. Wallet config is allowed or not
         2. WalletAmount was used in the failed payment or not.
         3. handleWalletToggle function check is needed in case of Retry payment failure bottomSheet, to not show wallet tile if neither upi_intent nor cod is available. */}
        {flowConfig?.wallet && walletAmountUsed && handleWalletToggle ? (
          <WalletFailureMethodComponent
            leadingIconUrl={getIcons("", ICONS_NAMES.WALLET_LOGO)}
            handleWalletToggle={handleWalletToggle}
            isWalletChecked={isWalletChecked}
            rightText={`₹${formatNumber(walletBalance)}`}
            title={walletTitle}
            theme={theme}
          />
        ) : null}

        {updatedNudge && (
          <SubTitle
            htmlText={updatedNudge}
            text={updatedNudge}
            font={theme?.typography?.subTitleSmall}
            color={theme?.colors?.titleColor}
            theme={theme}
            dataTestId="subTitle_bottomsheet"
          />
        )}
      </BottomSheetContentLayout>
    </>
  );
};
export default React.memo(FailureDetails);
